import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import type { Profile } from '@lr/shared';
import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { mergeGuestBasketIntoServer } from '../lib/guestBasket';

interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    profile?: { first_name?: string; last_name?: string; phone?: string }
  ) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadProfile() {
    try {
      const p = await apiFetch<Profile>('/api/account/me');
      setProfile(p);
    } catch {
      setProfile(null);
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (s?.user) {
        void loadProfile();
        // On a fresh sign-in (NOT an existing session reloading), merge
        // any items the user added as a guest into their server basket.
        if (event === 'SIGNED_IN') {
          void mergeGuestBasketIntoServer().then((added) => {
            if (added > 0) {
              window.dispatchEvent(new CustomEvent('lr:basket-changed'));
            }
          });
        }
      } else {
        setProfile(null);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session?.user) void loadProfile();
  }, [session?.user?.id]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      profile,
      session,
      loading,
      signIn: async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      },
      signUp: async (email, password, profile) => {
        // Pass first/last/phone through Supabase Auth user_metadata so
        // the public.profiles row trigger (handle_new_user) can pick
        // them up and populate the columns at signup time.
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: profile
              ? {
                  first_name: profile.first_name ?? null,
                  last_name: profile.last_name ?? null,
                  phone: profile.phone ?? null
                }
              : undefined
          }
        });
        if (error) throw error;
      },
      signOut: async () => {
        await supabase.auth.signOut();
      },
      refreshProfile: loadProfile
    }),
    [session, profile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
